import React, { RefCallback } from 'react'
import { Accordion, Box, Flex, Link, Text, useAppTheme } from '@workwhile/ui'
import { Calendar, Hash, MessageCircleQuestion } from 'lucide-react'
import { format } from 'date-fns'
import * as ROUTES from 'constants/routes'
import { AccordionCard } from 'components/AccordionCard'
import { FlexibleInfoTable } from './FlexibleInfoTable'
import { useAssessmentDetailContextValue } from '../context/AssessmentDetailProvider'
import { TranscriptProvider } from './Transcript/context/TranscriptProvider'
import { TranscriptMain } from './Transcript/components/TranscriptMain'

type AssessmentItem = ReturnType<
  typeof useAssessmentDetailContextValue
>['state']['assessmentCalls'][number]

type AssessmentSectionProps = {
  assessment: AssessmentItem
  audioRef: RefCallback<HTMLAudioElement>
  onPlay: () => void
}

export const AssessmentSection = ({
  assessment,
  audioRef,
  onPlay,
}: AssessmentSectionProps) => {
  const { colors, space } = useAppTheme()
  const {
    shiftId,
    startedAt,
    endedAt,
    aiSummary,
    recordingUrl,
    transcriptUrl,
    questions,
  } = assessment

  if (questions.length === 0) {
    return null
  }

  return (
    <AccordionCard
      title="Assessment Call"
      size="large"
      contentWrapperProps={{
        display: 'flex',
        flexDirection: 'column',
        gap: space[3],
      }}
    >
      <Flex flexDirection="column" gap={2}>
        <FlexibleInfoTable Icon={Calendar}>
          <Text fontSize={2}>
            Started at: {format(new Date(startedAt), 'MMM dd, yyyy, hh:mm a')}
          </Text>
          <Text fontSize={2}>
            Ended at: {format(new Date(endedAt), 'MMM dd, yyyy, hh:mm a')}
          </Text>
        </FlexibleInfoTable>
        <FlexibleInfoTable Icon={Hash}>
          <Text fontSize={2}>
            Shift:{' '}
            <Link
              to={ROUTES.SHIFT_DETAIL.replace(':shiftId', shiftId.toString())}
              target="_blank"
            >
              {shiftId}
            </Link>
          </Text>
        </FlexibleInfoTable>
      </Flex>

      <Box>
        <Text fontSize={1} color="lightText" mb={1}>
          Questions Asked
        </Text>
        <Flex flexDirection="column" gap={1}>
          {questions.map((question, index) => (
            <FlexibleInfoTable key={question.id} Icon={MessageCircleQuestion}>
              <Text fontSize={2}>
                {index + 1}. {question.name}
              </Text>
            </FlexibleInfoTable>
          ))}
        </Flex>
      </Box>

      {aiSummary ? (
        <Box>
          <Text fontSize={1} color="lightText" mb={1}>
            AI Summary
          </Text>
          <Text style={{ whiteSpace: 'pre-wrap' }}>{aiSummary}</Text>
        </Box>
      ) : null}

      <Flex flexDirection="column" minHeight={0} overflow="hidden">
        {recordingUrl ? (
          <Box>
            <audio
              controls
              style={{ width: '100%' }}
              ref={audioRef}
              onPlay={onPlay}
            >
              <source src={recordingUrl} type="audio/wav" />
              Your browser does not support the audio element.
            </audio>
          </Box>
        ) : (
          <Text color="lightText">No recording available.</Text>
        )}
        {transcriptUrl ? (
          <Accordion
            items={[
              {
                trigger: (
                  <Text color="primary" textAlign="right">
                    View Transcript
                  </Text>
                ),
                content: (
                  <Box
                    p={3}
                    height="400px"
                    overflowY="scroll"
                    borderRadius="standard"
                    borderColor={colors.neutrals[100]}
                  >
                    <TranscriptProvider transcriptUrl={transcriptUrl}>
                      <TranscriptMain />
                    </TranscriptProvider>
                  </Box>
                ),
              },
            ]}
          />
        ) : recordingUrl ? (
          <Text color="lightText" mt={2}>
            No transcript available.
          </Text>
        ) : null}
      </Flex>
    </AccordionCard>
  )
}
